import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import type { RunMetadataDto } from '@/api/control/missionImpactAPI.schemas';

interface SkipReasonDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Handler for dialog open/close state changes */
  onOpenChange: (open: boolean) => void;
  /** The run metadata containing skip reason details */
  run: RunMetadataDto | null;
}

/**
 * Reusable dialog component for displaying skip reason details
 * Shows skip reason with translated description
 */
export function SkipReasonDialog({ open, onOpenChange, run }: SkipReasonDialogProps) {
  const { t } = useLanguage();

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t.runs.skipReasonDialog.title}</DialogTitle>
          <DialogDescription>
            {t.runs.skipReasonDialog.runId} {run?.runId}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-2">{t.runs.skipReasonDialog.sourceTable}</h4>
            <p className="text-sm text-muted-foreground background-neutral p-3 rounded">
              {run?.sourceTableLogicalName ?? t.runs.skipReasonDialog.unknown}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium mb-2">{t.runs.skipReasonDialog.reason}</h4>
            <p className="text-sm text-muted-foreground background-neutral p-3 rounded">
              {run?.skipReason
                ? t.runs.skipReasons[run.skipReason] ||
                  run.skipReason
                : '-'}
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={handleClose}>
            {t.runs.skipReasonDialog.close}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
